import { useState } from 'react';
import {
  profileNeedsAction,
  type MiceChangeDateRequest,
  type MiceChooseInput,
  type MiceEventDays,
  type MiceNeedStatus,
  type MiceNeedView,
} from '@voll-intelligence/domain';
import { t } from '@web/i18n';
import { IconButton } from '@web/ui/core/atoms';
import { Button, ToneChip } from '@web/ui/core';
import { Drawer } from '@web/ui/core/organisms';
import { AgentConversation } from './agent_conversation';
import { CalcMemory } from './calc_memory';
import { EmailCorrection } from './email_correction';
import { NeedDates, canChangeDate, dateAlertDirection, type Direction } from './need_dates';
import { formatRoute } from './need_format';
import {
  MarkFooter,
  NeedLegs,
  agentPicks,
  legsOf,
  type CardPicks,
  type LegChoice,
  type ListedFlights,
  type NeedLeg,
} from './need_mark';
import { DrawerSection } from './need_recommendation';
import { NeedAlertChip, NeedErrorNote, NeedStatusChip } from './need_status_chip';

/** Status que aceitam marcar outro cartão (`POST /needs/:needId/choose`). */
const CHOOSABLE: ReadonlySet<MiceNeedStatus> = new Set<MiceNeedStatus>([
  'recommended',
  'no_solution',
  'approved',
]);

interface NeedDrawerProps {
  projectId: string;
  need: MiceNeedView | null;
  /** Datas do evento: a janela que o campo de nova data aceita. */
  event: MiceEventDays | null;
  /** Seção Cadastro: no topo quando o cadastro pede ação; senão, depois da recomendação. */
  profile?: React.ReactNode;
  canPurchase: boolean;
  /** Compras do evento abertas: encerradas, saem escolher outro voo, trocar data e pedir revisão
   *  (o servidor recusa); marcar para emitir a sugestão do agente continua. */
  purchasesOpen: boolean;
  reconsidering: boolean;
  onReconsider: (needId: string, comment: string, onDone: () => void) => void;
  /** "Marcar para emitir" com outro cartão em algum sentido: um só `choose`, com `approve`. */
  onChoose: (needId: string, choice: MiceChooseInput) => void;
  /** Uma escolha está indo ao servidor: os botões ficam desabilitados até a resposta. */
  choosing: boolean;
  /** Nova data de um sentido (`POST /needs/:needId/date`); `onDone` fecha o campo. */
  onChangeDate: (needId: string, change: MiceChangeDateRequest, onDone: () => void) => void;
  changingDate: boolean;
  /** E-mail corrigido da necessidade sem viajante; `onDone` fecha o campo. */
  onCorrectEmail: (needId: string, email: string, onDone: () => void) => void;
  correctingEmail: boolean;
  onClose: () => void;
  /** "Marcar para emitir" com os cartões do agente: aprova a sugestão. */
  onApprove: (needId: string) => void;
  /** Anterior/próximo dentro da lista filtrada. */
  onStep?: (delta: -1 | 1) => void;
  position?: { index: number; total: number };
}

function Navigation({
  position,
  onStep,
}: Readonly<Pick<NeedDrawerProps, 'position' | 'onStep'>>): React.JSX.Element | null {
  if (!position || !onStep) return null;
  return (
    <div className="flex items-center gap-1">
      <span className="text-micro text-muted-foreground">
        {t('mice.drawer.position', { index: position.index + 1, total: position.total })}
      </span>
      <IconButton
        icon="arrow-left"
        variant="ghost"
        size="sm"
        disabled={position.index === 0}
        aria-label={t('mice.drawer.prev')}
        title={t('mice.drawer.prev')}
        onClick={() => {
          onStep(-1);
        }}
      />
      <IconButton
        icon="arrow-right"
        variant="ghost"
        size="sm"
        disabled={position.index >= position.total - 1}
        aria-label={t('mice.drawer.next')}
        title={t('mice.drawer.next')}
        onClick={() => {
          onStep(1);
        }}
      />
    </div>
  );
}

type Choice = LegChoice & { legs: NeedLeg[] };

/** Os cartões por sentido e a memória de cálculo da versão vigente; ou "ainda sem sugestão". */
function RecommendationCards({
  need,
  projectId,
  choice,
}: Readonly<{ need: MiceNeedView; projectId: string; choice: Choice }>): React.JSX.Element {
  const rec = need.recommendation;
  if (!rec) {
    return (
      <p className="text-meta text-muted-foreground">{t('mice.needs.drawer.noRecommendation')}</p>
    );
  }
  return (
    <>
      {rec.noSolution ? <p className="text-meta text-destructive">{rec.noSolution}</p> : null}
      <NeedLegs need={need} projectId={projectId} legs={choice.legs} choice={choice} />
      {/* Sem decisões por sentido (recomendação antiga), o porquê é o texto da versão. */}
      {rec.directions.length === 0 ? (
        <p className="text-meta text-muted-foreground">{rec.reason}</p>
      ) : null}
      <CalcMemory rec={rec} />
    </>
  );
}

function NeedDetails(
  props: Readonly<NeedDrawerProps & { need: MiceNeedView; choice: Choice }>,
): React.JSX.Element {
  const { need } = props;
  const rec = need.recommendation;
  const profileFirst = profileNeedsAction(need.profileCheck);
  const [editingDate, setEditingDate] = useState<Direction | null>(null);
  const canChange = canChangeDate(need, props.canPurchase && props.purchasesOpen);
  return (
    <div className="flex flex-1 flex-col gap-6 overflow-y-auto px-5 py-5">
      {profileFirst ? props.profile : null}
      {need.preference || need.agencyNote ? (
        <DrawerSection title={t('mice.needs.table.preference')}>
          {need.preference ? <p className="text-meta text-foreground">{need.preference}</p> : null}
          {need.agencyNote ? (
            <p className="text-meta text-muted-foreground">{need.agencyNote}</p>
          ) : null}
        </DrawerSection>
      ) : null}
      <NeedDates
        need={need}
        event={props.event}
        canChange={canChange}
        busy={props.changingDate}
        editing={editingDate}
        onEdit={setEditingDate}
        onChange={(direction, date, onDone) => {
          if (!rec) return;
          props.onChangeDate(need.id, { direction, date, version: rec.version }, onDone);
        }}
      />
      <RecommendationCards need={need} projectId={props.projectId} choice={props.choice} />
      {rec && rec.alerts.length > 0 ? (
        <DrawerSection title={t('mice.needs.table.alerts')}>
          <ul className="flex flex-col gap-2">
            {rec.alerts.map((alert) => {
              const dateDirection = canChange ? dateAlertDirection(alert) : null;
              return (
                <li key={`${alert.code}:${alert.message}`} className="flex flex-col gap-1">
                  <NeedAlertChip alert={alert} />
                  <span className="text-meta text-muted-foreground">{alert.message}</span>
                  {dateDirection ? (
                    <div>
                      <Button
                        size="xs"
                        variant="outline"
                        onClick={() => {
                          setEditingDate(dateDirection);
                        }}
                      >
                        {t(
                          dateDirection === 'OUTWARD'
                            ? 'mice.needs.dates.changeOutbound'
                            : 'mice.needs.dates.changeInbound',
                        )}
                      </Button>
                    </div>
                  ) : null}
                </li>
              );
            })}
          </ul>
        </DrawerSection>
      ) : null}
      <AgentConversation
        need={need}
        canPurchase={props.canPurchase && props.purchasesOpen}
        busy={props.reconsidering}
        onAsk={(comment, onDone) => {
          props.onReconsider(need.id, comment, onDone);
        }}
      />
      {profileFirst ? null : props.profile}
    </div>
  );
}

/**
 * Corpo e rodapé da necessidade aberta. Quem monta passa `key` com a necessidade e a versão: o
 * cartão marcado em cada sentido volta à escolha do agente quando uma das duas muda.
 */
function NeedPanel(props: Readonly<NeedDrawerProps & { need: MiceNeedView }>): React.JSX.Element {
  const { need } = props;
  const rec = need.recommendation;
  const legs = rec ? legsOf(rec) : [];
  const [picks, setPicks] = useState<CardPicks>(() => agentPicks(legs));
  const [listed, setListed] = useState<ListedFlights>({});
  const markable = props.canPurchase && CHOOSABLE.has(need.status);
  const readOnly = !markable || !props.purchasesOpen;
  // Com as compras encerradas, uma troca marcada antes não vale mais (a escolha dá 409): volta a
  // sugestão do agente, que ainda pode ser aprovada.
  const shown = props.purchasesOpen ? picks : agentPicks(legs);
  const choice: Choice = {
    legs,
    picks: shown,
    listed,
    readOnly,
    onPick: (direction, pick) => {
      setPicks((current) => ({ ...current, [direction]: pick }));
    },
    onList: (direction, flight) => {
      setListed((current) => ({ ...current, [direction]: flight }));
      setPicks((current) => ({ ...current, [direction]: { id: flight.id, source: 'event_list' } }));
    },
  };
  return (
    <>
      <NeedDetails {...props} choice={choice} />
      {rec && markable && legs.length > 0 ? (
        <MarkFooter
          need={need}
          rec={rec}
          legs={legs}
          picks={shown}
          listed={listed}
          choosing={props.choosing}
          onApprove={props.onApprove}
          onChoose={props.onChoose}
        />
      ) : null}
    </>
  );
}

export function NeedDrawer(props: Readonly<NeedDrawerProps>): React.JSX.Element {
  const { need, onClose } = props;
  return (
    <Drawer
      open={need !== null}
      onClose={onClose}
      ariaLabel={t('mice.needs.drawer.title', { row: need?.rowNumber ?? '' })}
    >
      {need ? (
        <>
          <div className="flex flex-shrink-0 items-center gap-3 border-b border-border-soft px-5 py-4">
            <div className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="truncate font-serif text-drawer-title text-foreground">
                {need.travelerName ?? need.travelerEmail}
              </span>
              <span className="font-mono text-micro text-muted-foreground">
                {formatRoute(need)}
              </span>
              <NeedErrorNote need={need} />
              <EmailCorrection
                key={need.id}
                row={need}
                canPurchase={props.canPurchase}
                busy={props.correctingEmail}
                onCorrect={(email, onDone) => {
                  props.onCorrectEmail(need.id, email, onDone);
                }}
              />
            </div>
            {need.history.length > 1 && need.recommendation ? (
              <ToneChip tone="brand">
                {t('mice.drawer.revision', { version: need.recommendation.version })}
              </ToneChip>
            ) : null}
            <NeedStatusChip status={need.status} />
            <Navigation position={props.position} onStep={props.onStep} />
            <IconButton
              icon="close"
              variant="outline"
              onClick={onClose}
              title={t('mice.drawer.close')}
              aria-label={t('mice.drawer.close')}
            />
          </div>
          <NeedPanel
            key={`${need.id}:${String(need.recommendation?.version ?? 0)}`}
            {...props}
            need={need}
          />
        </>
      ) : null}
    </Drawer>
  );
}
